<template>
  <div class="pixso-page mechanical-solution" :class="{ 'mechanical-solution--en': isEnglish }">
    <section
      class="mechanical-solution__hero pixso-bleed"
      :style="heroStyle"
      aria-labelledby="mechanical-hero-title"
    >
      <div class="mechanical-solution__hero-copy">
        <h1 id="mechanical-hero-title" class="mechanical-solution__hero-title">
          {{ t('TraditionalMachineryOperation.hero.title') }}
        </h1>
      </div>
    </section>

    <section class="mechanical-solution__section mechanical-solution__section--pain">
      <div class="pixso-container mechanical-solution__section-head">
        <p class="pixso-eyebrow">{{ t('TraditionalMachineryOperation.sections.pain.eyebrow') }}</p>
        <h2 class="mechanical-solution__stack-title" data-text-reveal="inline">
          <span class="mechanical-solution__title-accent">{{ t('TraditionalMachineryOperation.sections.pain.titleAccent') }}</span>
          <span>{{ t('TraditionalMachineryOperation.sections.pain.title') }}</span>
        </h2>
      </div>

      <div class="pixso-container mechanical-solution__pain-shell">
        <AccordionImageGallery
          :items="painGalleryItems"
          :aria-label="t('TraditionalMachineryOperation.sections.pain.eyebrow')"
        />
      </div>
    </section>

    <section ref="howSectionRef" class="mechanical-solution__how pixso-bleed" :style="howStyle">
      <div class="pixso-stage mechanical-solution__how-stage">
        <div class="pixso-container mechanical-solution__section-head">
          <p class="pixso-eyebrow">{{ t('TraditionalMachineryOperation.sections.how.eyebrow') }}</p>
          <h2 class="mechanical-solution__stack-title" data-text-reveal="inline">
            <span class="mechanical-solution__title-accent">{{ t('TraditionalMachineryOperation.sections.how.titleAccent') }}</span>
            <span>{{ t('TraditionalMachineryOperation.sections.how.title') }}</span>
          </h2>
        </div>

        <div class="mechanical-solution__how-card-shell">
          <figure
            ref="howCardRef"
            class="mechanical-solution__media-card mechanical-solution__how-card"
            data-text-reveal="off"
            :aria-hidden="isHowVideoInteractive ? undefined : 'true'"
          >
            <video
              ref="howVideoRef"
              :key="isEnglish ? 'en' : 'zh'"
              class="mechanical-solution__video"
              :poster="howPosterImage"
              controls
              muted
              loop
              playsinline
              preload="metadata"
              :aria-label="t('TraditionalMachineryOperation.alt.howPosterImage')"
              @play="showHowVideoOverlay = false"
            >
              <source :src="isEnglish ? howVideoEn : howVideo" type="video/mp4" />
            </video>
            <button
              v-if="showHowVideoOverlay"
              class="mechanical-solution__video-overlay"
              type="button"
              :style="howVideoOverlayStyle"
              :aria-label="t('TraditionalMachineryOperation.alt.howPosterImage')"
              @click="playHowVideo"
            />
          </figure>
        </div>
      </div>
    </section>

    <section class="mechanical-solution__section">
      <div class="pixso-container mechanical-solution__section-head">
        <p class="pixso-eyebrow">{{ t('TraditionalMachineryOperation.sections.features.eyebrow') }}</p>
        <h2 class="mechanical-solution__stack-title" data-text-reveal="inline">
          <span class="mechanical-solution__title-accent">{{ t('TraditionalMachineryOperation.sections.features.titleAccent') }}</span>
          <span>{{ t('TraditionalMachineryOperation.sections.features.title') }}</span>
        </h2>
      </div>

      <FeatureMosaicGrid
        class="mechanical-solution__wide mechanical-solution__feature-shell"
        layout="mechanical"
        :items="featureMosaicItems"
        :aria-label="t('TraditionalMachineryOperation.sections.features.title')"
        gap="var(--mechanical-space-24)"
        card-radius="var(--pixso-radius-lg)"
        compact-card-radius="var(--mechanical-radius-sm)"
        data-text-reveal="off"
      />
    </section>

    <section class="mechanical-solution__section mechanical-solution__section--hardware">
      <div class="pixso-container mechanical-solution__section-head mechanical-solution__section-head--hardware">
        <p class="pixso-eyebrow">{{ t('TraditionalMachineryOperation.sections.hardware.eyebrow') }}</p>
        <h2 class="mechanical-solution__stack-title" data-text-reveal="inline">
          <span>{{ t('TraditionalMachineryOperation.sections.hardware.title') }}</span>
        </h2>
        <p class="mechanical-solution__section-desc mechanical-solution__section-desc--medium">
          {{ t('TraditionalMachineryOperation.sections.hardware.description') }}
        </p>
        <PixsoButton class="mechanical-solution__cta" :text="t('TraditionalMachineryOperation.sections.hardware.cta')" to="/about/contact" />
      </div>

      <HardwareCalloutScroller
        class="mechanical-solution__hardware-stage"
        :image-src="hardwareImage"
        :image-alt="t('TraditionalMachineryOperation.alt.hardwareImage')"
        :aria-label="t('TraditionalMachineryOperation.alt.hardwareImage')"
        :items="hardwareItems"
        :image-layout="hardwareImageLayout"
        stage-aspect-ratio="1627 / 840"
        :desktop-min-width="1201"
        :scroll-step="420"
        mobile-image-width="min(100%, 720px)"
        :scroll-hint-src="scrollHintIcon"
        scroll-hint-placement="viewport"
        scroll-hint-bottom="6vh"
      />
    </section>

    <section class="mechanical-solution__section">
      <div class="pixso-container mechanical-solution__section-head">
        <p class="pixso-eyebrow">{{ t('TraditionalMachineryOperation.sections.control.eyebrow') }}</p>
        <h2 class="mechanical-solution__stack-title" data-text-reveal="inline">
          <span>{{ t('TraditionalMachineryOperation.sections.control.title') }}</span>
        </h2>
        <p class="mechanical-solution__section-desc mechanical-solution__section-desc--wide">
          {{ t('TraditionalMachineryOperation.sections.control.description') }}
        </p>
      </div>

      <div class="pixso-container">
        <TabbedVideoPlayer
          :items="capabilityVideos"
          :poster="controlImage"
          :tablist-label="t('TraditionalMachineryOperation.sections.control.tablistAria')"
        />
      </div>
    </section>

    <section class="mechanical-solution__section">
      <div class="pixso-container mechanical-solution__section-head mechanical-solution__section-head--narrow">
        <p class="pixso-eyebrow">{{ t('TraditionalMachineryOperation.sections.platform.eyebrow') }}</p>
        <h2 class="mechanical-solution__stack-title" data-text-reveal="inline">
          <span class="mechanical-solution__title-accent">{{ t('TraditionalMachineryOperation.sections.platform.titleAccent') }}</span>
          <span>{{ t('TraditionalMachineryOperation.sections.platform.title') }}</span>
        </h2>
        <p class="mechanical-solution__section-desc mechanical-solution__section-desc--wide">
          {{ t('TraditionalMachineryOperation.sections.platform.description') }}
        </p>
      </div>

      <div class="mechanical-solution__wide mechanical-solution__platform-gallery">
        <PlatformGrid
          :items="platformCards"
          :desktop-columns="3"
          :tablet-columns="2"
          :mobile-columns="1"
          :show-label="false"
          :aria-label="t('TraditionalMachineryOperation.sections.platform.title')"
        />
      </div>
    </section>

    <section class="mechanical-solution__section mechanical-solution__section--last">
      <div class="mechanical-solution__wide mechanical-solution__section-head">
        <h2 class="mechanical-solution__stack-title" data-text-reveal="inline">
          <span>{{ t('TraditionalMachineryOperation.sections.scenario.title') }}</span>
        </h2>
      </div>

      <div class="mechanical-solution__wide mechanical-solution__scenario-gallery">
        <ScenarioGrid
          :items="scenarioCards"
          :aria-label="t('TraditionalMachineryOperation.sections.scenario.title')"
        />
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AccordionImageGallery from '@/components/pixso/AccordionImageGallery.vue'
import FeatureMosaicGrid from '@/components/pixso/FeatureMosaicGrid.vue'
import HardwareCalloutScroller from '@/components/pixso/HardwareCalloutScroller.vue'
import PixsoButton from '@/components/pixso/PixsoButton.vue'
import PlatformGrid from '@/components/pixso/PlatformGrid.vue'
import ScenarioGrid from '@/components/pixso/ScenarioGrid.vue'
import TabbedVideoPlayer from '@/components/pixso/TabbedVideoPlayer.vue'
import { bindPinnedVideoFullscreen } from '@/js/usePinnedVideoFullscreen'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

import heroImage from '@/assets/images/pixso-mechanical-solution/bannerproductinertia.webp'
import painLaborImage from '@/assets/images/pixso-mechanical-solution/pain-labor.jpg'
import painPrecisionImage from '@/assets/images/pixso-mechanical-solution/pain-precision.jpg'
import painEfficiencyImage from '@/assets/images/pixso-mechanical-solution/pain-efficiency.jpg'
import painRiskImage from '@/assets/images/pixso-mechanical-solution/pain-risk.jpg'
import howBackgroundImage from '@/assets/images/pixso-mechanical-solution/how-background.jpg'
import howPosterImage from '@/assets/images/pixso-mechanical-solution/how-background.jpg'
import howVideoOverlayImage from '@/assets/images/pixso-mechanical-solution/how-video-overlay.png'
import machineryDumpTruckImage from '@/assets/images/pixso-mechanical-solution/machinery-dump-truck.png'
import featurePlaceholderImage from '@/assets/images/pixso-mechanical-solution/feature-path-planning.png'
import featureSupervisionIcon from '@/assets/images/pixso-mechanical-solution/feature-icons/supervision.svg'
import featureSlamIcon from '@/assets/images/pixso-mechanical-solution/feature-icons/slam.svg'
import featureCruiseIcon from '@/assets/images/pixso-mechanical-solution/feature-icons/cruise.svg'
import scrollHintIcon from '@/assets/images/pixso-common/xiahua.svg'
import controlImage from '@/assets/images/pixso-mechanical-solution/control-positioning.jpg'
import platformTaskImage from '@/assets/images/pixso-industrial-solution/platform-task.jpg'
import platformControlImage from '@/assets/images/pixso-industrial-solution/platform-control.jpg'
import platformAlarmImage from '@/assets/images/pixso-industrial-solution/platform-alarm.jpg'
import platformDataImage from '@/assets/images/pixso-industrial-solution/platform-data.jpg'
import platformAlgorithmImage from '@/assets/images/pixso-industrial-solution/platform-algorithm.jpg'
import platformDeviceImage from '@/assets/images/pixso-industrial-solution/platform-device.jpg'
import scenarioRollerImage from '@/assets/images/pixso-mechanical-solution/scenario-roller.jpg'
import scenarioSweeperImage from '@/assets/images/pixso-mechanical-solution/scenario-sweeper.jpg'
import scenarioExcavatorImage from '@/assets/images/pixso-mechanical-solution/scenario-excavator.jpg'
import scenarioSeederImage from '@/assets/images/pixso-mechanical-solution/scenario-seeder.jpg'
import scenarioHarvesterImage from '@/assets/images/pixso-mechanical-solution/scenario-harvester.jpg'
import scenarioSnowplowImage from '@/assets/images/pixso-mechanical-solution/scenario-snowplow.jpg'
import howVideo from '@/assets/videos/pixso-mechanical-solution/agriculture-mining.mp4'
import howVideoEn from '@/assets/videos/pixso-mechanical-solution/agriculture-mining-en.mp4'
import precisePositioningVideo from '@/assets/videos/pixso-mechanical-solution/precise-positioning.mp4'
import precisePositioningVideoEn from '@/assets/videos/pixso-mechanical-solution/precise-positioning-en.mp4'
import pathPlanningVideo from '@/assets/videos/pixso-mechanical-solution/path-planning.mp4'
import pathPlanningVideoEn from '@/assets/videos/pixso-mechanical-solution/path-planning-en.mp4'
import autonomousCruiseVideo from '@/assets/videos/pixso-mechanical-solution/autonomous-cruise.mp4'
import autonomousCruiseVideoEn from '@/assets/videos/pixso-mechanical-solution/autonomous-cruise-en.mp4'
import threeDimensionalMappingVideo from '@/assets/videos/pixso-mechanical-solution/three-dimensional-mapping.mp4'
import threeDimensionalMappingVideoEn from '@/assets/videos/pixso-mechanical-solution/three-dimensional-mapping-en.mp4'
import intelligentSupervisionVideo from '@/assets/videos/pixso-mechanical-solution/intelligent-supervision.mp4'
import intelligentSupervisionVideoEn from '@/assets/videos/pixso-mechanical-solution/intelligent-supervision-en.mp4'

const featureMainImage = machineryDumpTruckImage
const hardwareImage = machineryDumpTruckImage

const heroStyle = computed(() => ({
  backgroundImage: `var(--mechanical-hero-overlay), url(${heroImage})`
}))

const howStyle = computed(() => ({
  backgroundImage: `linear-gradient(90deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 35.299%), linear-gradient(0deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 35.299%), linear-gradient(270deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 35.299%), linear-gradient(180deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 35.299%), url(${howBackgroundImage})`
}))

const howVideoOverlayStyle = computed(() => ({
  backgroundImage: `url(${howVideoOverlayImage})`
}))

const { t, locale } = useI18n()
const isEnglish = computed(() => locale.value === 'en')
const howSectionRef = ref(null)
const howCardRef = ref(null)
const howVideoRef = ref(null)
const isHowVideoInteractive = ref(false)
const showHowVideoOverlay = ref(true)

const HOW_VIDEO_HIDDEN_DURATION = 0.04
const HOW_VIDEO_REVEAL_DURATION = 0.14
const HOW_VIDEO_HOLD_DURATION = 0.12
const HOW_VIDEO_TIMELINE_DURATION = HOW_VIDEO_HIDDEN_DURATION + HOW_VIDEO_REVEAL_DURATION + HOW_VIDEO_HOLD_DURATION
const HOW_VIDEO_INTERACTIVE_PROGRESS = (
  HOW_VIDEO_HIDDEN_DURATION + HOW_VIDEO_REVEAL_DURATION
) / HOW_VIDEO_TIMELINE_DURATION
const HOW_VIDEO_PIN_END = '+=60%'

let howRevealTimeline = null
let howVideoFullscreenCleanup = null

const playHowVideo = async () => {
  const video = howVideoRef.value

  if (!video) {
    return
  }

  showHowVideoOverlay.value = false

  try {
    await video.play()
  } catch {
    showHowVideoOverlay.value = true
  }
}

const bindHowVideoFullscreenSupport = (video) => {
  howVideoFullscreenCleanup?.()
  howVideoFullscreenCleanup = bindPinnedVideoFullscreen(video, {
    getScrollTrigger: () => howRevealTimeline?.scrollTrigger,
    getSettleTargets: () => [howSectionRef.value, howCardRef.value]
  })
}

onMounted(() => {
  const section = howSectionRef.value
  const card = howCardRef.value
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  if (!section || !card) {
    bindHowVideoFullscreenSupport(howVideoRef.value)
    return
  }

  if (reducedMotion) {
    gsap.set(card, { autoAlpha: 1, y: 0, scale: 1, pointerEvents: 'auto' })
    isHowVideoInteractive.value = true
    bindHowVideoFullscreenSupport(howVideoRef.value)
    return
  }

  gsap.set(card, {
    autoAlpha: 0,
    y: 96,
    scale: 0.94,
    pointerEvents: 'none'
  })

  howRevealTimeline = gsap.timeline({
    scrollTrigger: {
      trigger: section,
      start: 'top top',
      end: HOW_VIDEO_PIN_END,
      scrub: true,
      pin: true,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onUpdate: (self) => {
        isHowVideoInteractive.value = self.progress >= HOW_VIDEO_INTERACTIVE_PROGRESS
      }
    }
  })

  howRevealTimeline
    .to(card, { autoAlpha: 0, y: 96, scale: 0.94, duration: HOW_VIDEO_HIDDEN_DURATION, ease: 'none' })
    .to(card, { autoAlpha: 1, y: 0, scale: 1, pointerEvents: 'auto', duration: HOW_VIDEO_REVEAL_DURATION, ease: 'power2.out' })
    .to(card, { autoAlpha: 1, y: 0, scale: 1, duration: HOW_VIDEO_HOLD_DURATION, ease: 'none' })

  bindHowVideoFullscreenSupport(howVideoRef.value)
})

watch(isEnglish, async () => {
  await nextTick()
  bindHowVideoFullscreenSupport(howVideoRef.value)
})

onBeforeUnmount(() => {
  howVideoFullscreenCleanup?.()
  howVideoFullscreenCleanup = null
  howRevealTimeline?.scrollTrigger?.kill()
  howRevealTimeline?.kill()
})

const painItems = computed(() => [
  {
    key: 'labor',
    initial: t('TraditionalMachineryOperation.painItems.labor.initial'),
    title: t('TraditionalMachineryOperation.painItems.labor.title'),
    description: t('TraditionalMachineryOperation.painItems.labor.description'),
    image: painLaborImage
  },
  {
    key: 'precision',
    initial: t('TraditionalMachineryOperation.painItems.precision.initial'),
    title: t('TraditionalMachineryOperation.painItems.precision.title'),
    description: t('TraditionalMachineryOperation.painItems.precision.description'),
    image: painPrecisionImage
  },
  {
    key: 'efficiency',
    initial: t('TraditionalMachineryOperation.painItems.efficiency.initial'),
    title: t('TraditionalMachineryOperation.painItems.efficiency.title'),
    description: t('TraditionalMachineryOperation.painItems.efficiency.description'),
    image: painEfficiencyImage
  },
  {
    key: 'risk',
    initial: t('TraditionalMachineryOperation.painItems.risk.initial'),
    title: t('TraditionalMachineryOperation.painItems.risk.title'),
    description: t('TraditionalMachineryOperation.painItems.risk.description'),
    image: painRiskImage
  }
])

const painGalleryItems = computed(() => painItems.value.map((item) => ({
  key: item.key,
  initial: item.initial,
  title: item.title,
  body: item.description,
  image: item.image,
  imageAlt: t('TraditionalMachineryOperation.alt.painImage'),
  imagePosition: {
    labor: '60% center',
    precision: '70% 60%',
    efficiency: '0% 70%',
    risk: '60% center'
  }[item.key] || undefined
})))

const featureMosaicItems = computed(() => [
  {
    key: 'machinery',
    variant: 'hero',
    group: 'hero',
    image: featureMainImage,
    imageAlt: t('TraditionalMachineryOperation.alt.featureMainImage'),
    imageFit: 'contain'
  },
  {
    key: 'supervision',
    variant: 'action',
    group: 'topFull',
    iconSrc: featureSupervisionIcon,
    title: t('TraditionalMachineryOperation.features.supervision.title'),
    description: t('TraditionalMachineryOperation.features.supervision.description')
  },
  {
    key: 'slam',
    variant: 'action',
    group: 'topCards',
    iconSrc: featureSlamIcon,
    title: t('TraditionalMachineryOperation.features.slam.title'),
    description: t('TraditionalMachineryOperation.features.slam.description')
  },
  {
    key: 'cruise',
    variant: 'action',
    group: 'topCards',
    iconSrc: featureCruiseIcon,
    title: t('TraditionalMachineryOperation.features.cruise.title'),
    description: t('TraditionalMachineryOperation.features.cruise.description')
  },
  {
    key: 'path-planning',
    variant: 'image',
    group: 'topNarrow',
    tone: 'photo',
    image: featurePlaceholderImage,
    imageAlt: t('TraditionalMachineryOperation.features.pathPlanning.title'),
    title: t('TraditionalMachineryOperation.features.pathPlanning.title')
  }
])

const hardwareStageY = (percent) => {
  const fullStageHeight = 1270
  const croppedStageTop = 430
  const croppedStageHeight = 840

  return (((percent / 100) * fullStageHeight - croppedStageTop) / croppedStageHeight) * 100
}

const hardwareStageHeight = (percent) => {
  const fullStageHeight = 1270
  const croppedStageHeight = 840

  return ((percent / 100) * fullStageHeight / croppedStageHeight) * 100
}

const hardwareImageLayout = {
  left: 27.36,
  top: hardwareStageY(49),
  width: 45.79,
  height: hardwareStageHeight(39.06)
}

const hardwareItems = computed(() => [
  {
    key: 'camera',
    title: t('TraditionalMachineryOperation.hardware.camera.title'),
    note: { x: 0, y: hardwareStageY(42.13), width: 18 },
    dot: { x: 48.15, y: hardwareStageY(59.65) },
    line: {
      from: { anchor: 'title-end', gap: 14 },
      to: { x: 48.15, y: hardwareStageY(59.65) }
    }
  },
  {
    key: 'sensor',
    title: t('TraditionalMachineryOperation.hardware.sensor.title'),
    side: 'right',
    note: { x: 73.51, y: hardwareStageY(48.82), width: 26.49 },
    dot: { x: 54.45, y: hardwareStageY(58.55) },
    line: {
      from: { anchor: 'title-start', gap: 14 },
      to: { x: 54.45, y: hardwareStageY(58.55) }
    },
    lines: [
      [{ text: t('TraditionalMachineryOperation.hardware.sensor.description') }]
    ]
  },
  {
    key: 'edge',
    title: t('TraditionalMachineryOperation.hardware.edge.title'),
    side: 'right',
    note: { x: 73.51, y: hardwareStageY(59.29), width: 26.49 },
    dot: { x: 54.88, y: hardwareStageY(70.65) },
    line: {
      from: { anchor: 'title-start', gap: 14 },
      to: { x: 54.88, y: hardwareStageY(70.65) }
    },
    lines: [
      [{ text: t('TraditionalMachineryOperation.hardware.edge.line1') }],
      [{ text: t('TraditionalMachineryOperation.hardware.edge.line2') }],
      [{ text: t('TraditionalMachineryOperation.hardware.edge.line3') }]
    ]
  },
  {
    key: 'lidar',
    title: t('TraditionalMachineryOperation.hardware.lidar.title'),
    note: { x: 6.95, y: hardwareStageY(59.29), width: 26.49 },
    dot: { x: 46.1, y: hardwareStageY(67.72) },
    line: {
      from: { anchor: 'title-end', gap: 14 },
      to: { x: 46.1, y: hardwareStageY(67.72) }
    },
    lines: [
      [{ text: t('TraditionalMachineryOperation.hardware.lidar.line1') }],
      [{ text: t('TraditionalMachineryOperation.hardware.lidar.line2') }],
      [{ text: t('TraditionalMachineryOperation.hardware.lidar.line3'), highlight: true }]
    ]
  }
])

const scenarioCards = computed(() => [
  { key: 'roller', title: t('TraditionalMachineryOperation.scenarioCards.roller'), image: scenarioRollerImage, alt: t('TraditionalMachineryOperation.scenarioCards.roller') },
  { key: 'sweeper', title: t('TraditionalMachineryOperation.scenarioCards.sweeper'), image: scenarioSweeperImage, alt: t('TraditionalMachineryOperation.scenarioCards.sweeper') },
  { key: 'excavator', title: t('TraditionalMachineryOperation.scenarioCards.excavator'), image: scenarioExcavatorImage, alt: t('TraditionalMachineryOperation.scenarioCards.excavator') },
  { key: 'seeder', title: t('TraditionalMachineryOperation.scenarioCards.seeder'), image: scenarioSeederImage, alt: t('TraditionalMachineryOperation.scenarioCards.seeder') },
  { key: 'harvester', title: t('TraditionalMachineryOperation.scenarioCards.harvester'), image: scenarioHarvesterImage, alt: t('TraditionalMachineryOperation.scenarioCards.harvester') },
  { key: 'snowplow', title: t('TraditionalMachineryOperation.scenarioCards.snowplow'), image: scenarioSnowplowImage, alt: t('TraditionalMachineryOperation.scenarioCards.snowplow') }
])

const platformCards = computed(() => [
  {
    key: 'platform-task',
    image: platformTaskImage,
    alt: t('TraditionalMachineryOperation.alt.platformTask')
  },
  {
    key: 'platform-control',
    image: platformControlImage,
    alt: t('TraditionalMachineryOperation.alt.platformControl')
  },
  {
    key: 'platform-alarm',
    image: platformAlarmImage,
    alt: t('TraditionalMachineryOperation.alt.platformAlarm')
  },
  {
    key: 'platform-data',
    image: platformDataImage,
    alt: t('TraditionalMachineryOperation.alt.platformData')
  },
  {
    key: 'platform-algorithm',
    image: platformAlgorithmImage,
    alt: t('TraditionalMachineryOperation.alt.platformAlgorithm')
  },
  {
    key: 'platform-device',
    image: platformDeviceImage,
    alt: t('TraditionalMachineryOperation.alt.platformDevice')
  }
])

const capabilityVideos = computed(() => [
  {
    key: 'position',
    buttonLabel: t('TraditionalMachineryOperation.capabilityTabs.position'),
    src: isEnglish.value ? precisePositioningVideoEn : precisePositioningVideo
  },
  {
    key: 'path',
    buttonLabel: t('TraditionalMachineryOperation.capabilityTabs.path'),
    src: isEnglish.value ? pathPlanningVideoEn : pathPlanningVideo
  },
  {
    key: 'cruise',
    buttonLabel: t('TraditionalMachineryOperation.capabilityTabs.cruise'),
    src: isEnglish.value ? autonomousCruiseVideoEn : autonomousCruiseVideo
  },
  {
    key: 'slam',
    buttonLabel: t('TraditionalMachineryOperation.capabilityTabs.slam'),
    src: isEnglish.value ? threeDimensionalMappingVideoEn : threeDimensionalMappingVideo
  },
  {
    key: 'supervision',
    buttonLabel: t('TraditionalMachineryOperation.capabilityTabs.supervision'),
    src: isEnglish.value ? intelligentSupervisionVideoEn : intelligentSupervisionVideo
  }
])
</script>

<style scoped>
.mechanical-solution {
  --mechanical-hero-overlay: linear-gradient(180deg, rgba(0, 0, 0, 0.16) 0%, rgba(0, 0, 0, 0.34) 100%);
  --mechanical-how-overlay: linear-gradient(180deg, rgba(0, 0, 0, 0.12) 0%, rgba(0, 0, 0, 0.28) 100%);
  --mechanical-radius-sm: var(--pixso-radius-md);
  --mechanical-space-12: var(--pixso-gap-sm);
  --mechanical-space-24: var(--pixso-gap-md);
  --mechanical-space-48: var(--pixso-gap-lg);
  --mechanical-space-96: var(--pixso-fs-96);
  --mechanical-page-end-space: var(--pixso-section-gap-xl);
  --mechanical-section-space: var(--pixso-section-gap-xl);
  --mechanical-pain-top-space: var(--mechanical-space-96);
  --mechanical-pain-bottom-space: var(--pixso-section-gap-xl);
  --mechanical-flow-gap: var(--pixso-gap-lg);
  --mechanical-hardware-stage-width: 1627px;
  --mechanical-copy-width: 1200px;
  --mechanical-copy-inner-width: 1152px;
  --mechanical-wide-width: 1792px;
  padding-bottom: var(--mechanical-page-end-space);
  font-family: var(--pixso-font-cn);
}

.mechanical-solution__wide {
  width: min(calc(100% - var(--pixso-page-padding) * 2), var(--mechanical-wide-width));
  margin: 0 auto;
}

.mechanical-solution__hero {
  position: relative;
  width: 100vw;
  margin-left: calc(50% - 50vw);
  height: var(--pixso-hero-height);
  min-height: var(--pixso-hero-min);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  overflow: hidden;
}

.mechanical-solution__hero-copy {
  position: absolute;
  z-index: 1;
  top: clamp(96px, 12vw, 192px);
  left: 50%;
  width: min(calc(100% - var(--pixso-page-padding)), 1120px);
  transform: translateX(-50%);
  text-align: center;
  pointer-events: none;
}

.mechanical-solution__hero-title {
  margin: 0;
  color: var(--pixso-color-text);
  font-family: var(--pixso-font-cn);
  font-weight: 700;
  font-size: clamp(var(--pixso-fs-48), 5vw, var(--pixso-fs-96));
  line-height: clamp(var(--pixso-lh-48), 6vw, var(--pixso-lh-96));
}

.mechanical-solution__section {
  padding-top: var(--mechanical-section-space);
}

.mechanical-solution__section--pain {
  padding-top: var(--mechanical-pain-top-space);
  padding-bottom: var(--mechanical-pain-bottom-space);
}

.mechanical-solution__section--last {
  padding-bottom: 0;
}

.mechanical-solution__section-head {
  --pixso-container: var(--mechanical-copy-width);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--mechanical-space-12);
  padding-inline: var(--mechanical-space-24);
  text-align: center;
}

.mechanical-solution__section-head--left {
  align-items: flex-start;
  text-align: left;
}

.mechanical-solution__section-head--narrow {
  max-width: var(--mechanical-copy-width);
}

.mechanical-solution__section-head.mechanical-solution__section-head--hardware {
  gap: 0;
}

.mechanical-solution__section-head--hardware .mechanical-solution__stack-title,
.mechanical-solution__section-head--hardware .mechanical-solution__section-desc {
  margin-top: var(--mechanical-space-12);
}

.mechanical-solution__section-head--hardware .mechanical-solution__cta {
  margin-top: var(--mechanical-space-48);
}

.mechanical-solution__stack-title {
  width: 100%;
  max-width: min(100%, 1120px);
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
}

.mechanical-solution__stack-title--left {
  align-items: flex-start;
}

.mechanical-solution__stack-title > span {
  max-width: 100%;
  font-family: var(--pixso-font-cn);
  font-weight: 700;
  font-size: clamp(var(--pixso-fs-48), 5vw, var(--pixso-fs-96));
  line-height: clamp(var(--pixso-lh-48), 6vw, var(--pixso-lh-96));
  text-align: center;
  overflow-wrap: anywhere;
  text-wrap: balance;
}

.mechanical-solution__stack-title--left span {
  text-align: left;
}

.mechanical-solution__title-accent,
.mechanical-solution__highlight {
  color: var(--pixso-color-primary);
}

.mechanical-solution__section-desc {
  margin: 0;
  color: var(--pixso-color-muted);
  font-family: var(--pixso-font-cn);
  font-weight: 700;
  font-size: clamp(var(--pixso-fs-16), 1.25vw, var(--pixso-fs-24));
  line-height: clamp(var(--pixso-lh-16), 1.77vw, var(--pixso-lh-24));
}

.mechanical-solution__section-desc--medium {
  max-width: var(--mechanical-copy-inner-width);
}

.mechanical-solution__section-desc--wide {
  max-width: var(--mechanical-copy-inner-width);
}

.mechanical-solution__pain-shell {
  --pixso-container: var(--mechanical-copy-width);
  margin-top: var(--mechanical-flow-gap);
}

.mechanical-solution__media-card {
  position: relative;
  border-radius: var(--pixso-radius-lg);
  overflow: hidden;
  background: var(--pixso-color-surface);
}

.mechanical-solution__media-card img {
  width: 100%;
  display: block;
  object-fit: cover;
}

.mechanical-solution__video {
  width: 100%;
  height: 100%;
  min-height: inherit;
  display: block;
  object-fit: cover;
}

.mechanical-solution__video-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: block;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  border-radius: inherit;
  overflow: hidden;
  background-color: #000;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  cursor: pointer;
}

.mechanical-solution__video-overlay:focus-visible {
  outline: 2px solid var(--pixso-color-primary);
  outline-offset: -6px;
}

.mechanical-solution__how {
  min-height: 100vh;
  min-height: 100svh;
  margin-top: 0;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.mechanical-solution__how-stage {
  min-height: inherit;
  box-sizing: border-box;
  padding-block: clamp(88px, 12vh, 132px) clamp(24px, 5vh, 56px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: clamp(28px, 4.5vh, 72px);
}

.mechanical-solution__how .mechanical-solution__section-head {
  padding-top: 0;
}

.mechanical-solution__how-card-shell {
  width: min(calc(100% - var(--mechanical-space-48)), clamp(560px, 48vw, 860px));
  margin-top: 0;
}

.mechanical-solution__how-card {
  width: 100%;
  max-height: min(54vh, 540px);
  margin: 0;
  box-shadow: 0 24px 90px rgba(0, 0, 0, 0.45);
  opacity: 0;
  transform: translate3d(0, 96px, 0) scale(0.94);
  transform-origin: center bottom;
  pointer-events: none;
  will-change: opacity, transform;
}

.mechanical-solution__how-card[aria-hidden="true"] {
  pointer-events: none;
}

.mechanical-solution__how-card .mechanical-solution__video {
  aspect-ratio: 16 / 9;
  max-height: min(54vh, 540px);
}

.mechanical-solution__feature-shell,
.mechanical-solution__platform-gallery,
.mechanical-solution__scenario-gallery {
  margin-top: var(--mechanical-flow-gap);
}

.mechanical-solution__feature-shell :deep(.feature-mosaic-card--action) {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 2px;
  padding: var(--pixso-gap-sm);
}

.mechanical-solution__feature-shell :deep(.feature-mosaic-card--action .feature-mosaic-card__text) {
  min-width: 0;
  width: 100%;
  /* 40px corner icon + gap; keep copy clear of the absolute SVG. */
  padding-right: calc(40px + var(--pixso-gap-sm));
  padding-bottom: calc(40px + var(--pixso-gap-xs));
}

.mechanical-solution__feature-shell :deep(.feature-mosaic-card--action .feature-mosaic-card__text p) {
  margin-top: 2px;
  color: rgba(255, 255, 255, 0.78);
  font-weight: 400;
}

.mechanical-solution__feature-shell :deep(.feature-mosaic-card--action .feature-mosaic-card__icon--route) {
  position: absolute;
  right: var(--pixso-gap-sm);
  bottom: var(--pixso-gap-sm);
  width: 40px;
  height: 40px;
  margin-top: 0;
  align-self: auto;
  flex-shrink: 0;
}

.mechanical-solution__feature-shell :deep(.feature-mosaic-card--photo .feature-mosaic-card__image-copy) {
  inset: var(--pixso-gap-md) auto auto var(--pixso-gap-md);
  right: var(--pixso-gap-md);
}

.mechanical-solution__feature-shell :deep(.feature-mosaic-card--photo .feature-mosaic-card__image-copy h3) {
  max-width: 100%;
  font-size: clamp(var(--pixso-fs-18), 1.4vw, var(--pixso-fs-24));
  line-height: clamp(var(--pixso-lh-18), 1.7vw, var(--pixso-lh-24));
  font-weight: 700;
  text-shadow: 0 2px 14px rgba(0, 0, 0, 0.58);
}

.mechanical-solution__feature-shell :deep(.feature-mosaic-card--photo::after) {
  background:
    linear-gradient(180deg, rgba(0, 0, 0, 0.42) 0%, rgba(0, 0, 0, 0.08) 36%, rgba(0, 0, 0, 0.18) 100%),
    linear-gradient(90deg, rgba(0, 0, 0, 0.28) 0%, rgba(0, 0, 0, 0.04) 56%, rgba(0, 0, 0, 0.12) 100%);
}

.mechanical-solution__hardware-stage {
  width: min(calc(100% - var(--pixso-page-padding) * 2), var(--mechanical-hardware-stage-width));
  margin: clamp(56px, 6vh, 96px) auto 0;
}

.mechanical-solution--en :deep(.hardware-callout-scroller__note h3) {
  font-size: clamp(var(--pixso-fs-28), 2.6vw, var(--pixso-fs-48));
  line-height: clamp(var(--pixso-lh-28), 3.1vw, var(--pixso-lh-48));
  font-weight: 800;
}

.mechanical-solution--en :deep(.hardware-callout-scroller__note p) {
  font-size: clamp(var(--pixso-fs-12), 0.95vw, var(--pixso-fs-16));
  line-height: clamp(var(--pixso-lh-12), 1.34vw, var(--pixso-lh-16));
}

/*
 * Desktop feature mosaic (shared layout for zh/en; only type size differs by locale):
 * 1) Hero keeps 884/630 as the only height authority.
 * 2) Middle/tall must NOT contribute min-content. height:0 + min-height:100% fills hero row.
 * 3) Compact padding + 40px corner icon so copy does not collide with SVG.
 */
.mechanical-solution__feature-shell
  :deep(.feature-mosaic-grid__desktop--mechanical .feature-mosaic-grid__mechanical-row) {
  align-items: stretch;
}

.mechanical-solution__feature-shell
  :deep(.feature-mosaic-grid__desktop--mechanical .feature-mosaic-card--hero) {
  align-self: start;
  justify-self: stretch;
  width: 100%;
  max-width: 100%;
  height: auto;
  min-height: 0;
  aspect-ratio: 884 / 630;
}

.mechanical-solution__feature-shell
  :deep(.feature-mosaic-grid__desktop--mechanical .feature-mosaic-grid__mechanical-middle) {
  /* Zero intrinsic height so multi-line copy cannot raise the grid row. */
  height: 0;
  min-height: 100%;
  max-height: 100%;
  overflow: hidden;
  align-self: stretch;
  grid-template-rows: minmax(0, 1.15fr) minmax(0, 1fr);
}

.mechanical-solution__feature-shell
  :deep(.feature-mosaic-grid__desktop--mechanical .feature-mosaic-grid__mechanical-tall) {
  height: 0;
  min-height: 100%;
  max-height: 100%;
  overflow: hidden;
  align-self: stretch;
}

.mechanical-solution__feature-shell
  :deep(.feature-mosaic-grid__desktop--mechanical .feature-mosaic-card--group-topFull),
.mechanical-solution__feature-shell
  :deep(.feature-mosaic-grid__desktop--mechanical .feature-mosaic-card--group-topCards) {
  overflow: hidden;
  min-height: 0;
  height: 100%;
}

.mechanical-solution__feature-shell
  :deep(.feature-mosaic-grid__desktop--mechanical .feature-mosaic-card--group-topNarrow) {
  flex: 1 1 auto;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.mechanical-solution__feature-shell
  :deep(.feature-mosaic-grid__desktop--mechanical .feature-mosaic-card--hero .feature-mosaic-card__media) {
  object-fit: contain;
  object-position: center;
}

/* Desktop type layout shared; Chinese sizes below, EN overrides only font metrics. */
.mechanical-solution__feature-shell
  :deep(.feature-mosaic-grid__desktop--mechanical .feature-mosaic-card--action .feature-mosaic-card__text h3) {
  font-size: var(--pixso-fs-16);
  line-height: var(--pixso-lh-16);
  font-weight: 700;
  white-space: normal;
  word-break: normal;
  overflow-wrap: normal;
  hyphens: none;
}

.mechanical-solution__feature-shell
  :deep(.feature-mosaic-grid__desktop--mechanical .feature-mosaic-card--action .feature-mosaic-card__text p) {
  margin-top: 2px;
  font-size: var(--pixso-fs-12);
  line-height: var(--pixso-lh-12);
  font-weight: 400;
  white-space: normal;
  word-break: normal;
  overflow-wrap: normal;
  hyphens: none;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}

.mechanical-solution__feature-shell
  :deep(.feature-mosaic-grid__desktop--mechanical .feature-mosaic-card--photo .feature-mosaic-card__image-copy h3) {
  white-space: normal;
  overflow-wrap: normal;
  hyphens: none;
}

/* EN desktop: only type metrics differ from Chinese. */
.mechanical-solution--en .mechanical-solution__feature-shell
  :deep(.feature-mosaic-grid__desktop--mechanical .feature-mosaic-card--action .feature-mosaic-card__text h3) {
  font-size: var(--pixso-fs-14);
  line-height: var(--pixso-lh-14);
  font-weight: 400;
}

.mechanical-solution--en .mechanical-solution__feature-shell
  :deep(.feature-mosaic-grid__desktop--mechanical .feature-mosaic-card--action .feature-mosaic-card__text p) {
  font-size: var(--pixso-fs-10);
  line-height: var(--pixso-lh-10);
  font-weight: 400;
}

.mechanical-solution--en .mechanical-solution__feature-shell
  :deep(.feature-mosaic-grid__desktop--mechanical .feature-mosaic-card--photo .feature-mosaic-card__image-copy h3) {
  font-size: var(--pixso-fs-14);
  line-height: var(--pixso-lh-14);
  font-weight: 400;
}

.mechanical-solution__platform-gallery {
  display: grid;
}

@media (max-width: 1439px) {
  .mechanical-solution__hardware-stage {
    width: min(calc(100% - var(--pixso-page-padding) * 2), 1200px);
  }
}

@media (max-width: 1200px) {
  .mechanical-solution__hardware-stage {
    width: min(calc(100% - var(--pixso-page-padding) * 2), 920px);
    margin-top: clamp(72px, 5vw, 96px);
  }

  /*
   * Compact mosaic: hero full-width, right cluster height follows content.
   * Layout is shared for zh/en; only type size differs by locale.
   */
  .mechanical-solution__feature-shell
    :deep(.feature-mosaic-grid__mobile--mechanical .feature-mosaic-card--hero) {
    width: 100%;
    max-height: min(42vh, 380px);
    aspect-ratio: 884 / 630;
  }

  .mechanical-solution__feature-shell
    :deep(.feature-mosaic-grid__mobile--mechanical .feature-mosaic-card--hero .feature-mosaic-card__media) {
    object-fit: contain;
    object-position: center;
  }

  .mechanical-solution__feature-shell
    :deep(.feature-mosaic-grid__mobile--mechanical .feature-mosaic-grid__mechanical-mobile-panel) {
    aspect-ratio: auto;
  }

  .mechanical-solution__feature-shell
    :deep(.feature-mosaic-grid__mobile--mechanical .feature-mosaic-grid__mechanical-middle) {
    grid-template-rows: auto auto;
  }

  .mechanical-solution__feature-shell
    :deep(.feature-mosaic-grid__mobile--mechanical .feature-mosaic-card--action) {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    gap: clamp(2px, 1cqw, 6px);
    min-height: clamp(96px, 26vw, 132px);
    aspect-ratio: auto;
    height: auto;
    padding: clamp(10px, 2.8cqw, 16px);
  }

  .mechanical-solution__feature-shell
    :deep(.feature-mosaic-grid__mobile--mechanical .feature-mosaic-card--group-topCards.feature-mosaic-card--action) {
    min-height: clamp(108px, 28vw, 148px);
  }

  .mechanical-solution__feature-shell
    :deep(.feature-mosaic-grid__mobile--mechanical .feature-mosaic-card--action .feature-mosaic-card__text) {
    min-width: 0;
    width: 100%;
    /* Icon is in document flow below; no corner reserve. */
    padding-right: 0;
    padding-bottom: 0;
  }

  .mechanical-solution__feature-shell
    :deep(.feature-mosaic-grid__mobile--mechanical .feature-mosaic-card--action .feature-mosaic-card__text h3) {
    max-width: 100%;
    font-size: clamp(var(--pixso-fs-12), 3.1vw, var(--pixso-fs-16));
    line-height: clamp(var(--pixso-lh-12), 3.8vw, var(--pixso-lh-16));
    white-space: normal;
    word-break: normal;
    overflow-wrap: normal;
    hyphens: none;
    display: block;
    overflow: visible;
  }

  .mechanical-solution__feature-shell
    :deep(.feature-mosaic-grid__mobile--mechanical .feature-mosaic-card--group-topCards .feature-mosaic-card__text h3) {
    font-size: clamp(var(--pixso-fs-10), 2.8vw, var(--pixso-fs-14));
    line-height: clamp(var(--pixso-lh-10), 3.4vw, var(--pixso-lh-14));
    white-space: normal;
    overflow-wrap: normal;
    hyphens: none;
  }

  .mechanical-solution__feature-shell
    :deep(.feature-mosaic-grid__mobile--mechanical .feature-mosaic-card--action .feature-mosaic-card__text p) {
    margin-top: clamp(2px, 0.8cqw, 4px);
    font-size: clamp(var(--pixso-fs-10), 2.4vw, var(--pixso-fs-12));
    line-height: clamp(var(--pixso-lh-10), 3vw, var(--pixso-lh-12));
    white-space: normal;
    word-break: normal;
    overflow-wrap: normal;
    hyphens: none;
    display: block;
    overflow: visible;
    -webkit-line-clamp: unset;
  }

  .mechanical-solution__feature-shell
    :deep(.feature-mosaic-grid__mobile--mechanical .feature-mosaic-card--action .feature-mosaic-card__icon--route) {
    position: static;
    margin-top: auto;
    align-self: flex-end;
    flex-shrink: 0;
    /* Fixed size: 14cqw made wide topFull cards hit 44px while half-width cards stayed ~29px. */
    width: 40px;
    height: 40px;
  }

  .mechanical-solution__feature-shell
    :deep(.feature-mosaic-grid__mobile--mechanical .feature-mosaic-card--group-topNarrow) {
    height: auto;
    min-height: 100%;
    align-self: stretch;
  }

  .mechanical-solution__feature-shell
    :deep(.feature-mosaic-grid__mobile--mechanical .feature-mosaic-card--photo) {
    min-height: 0;
    height: 100%;
    aspect-ratio: auto;
  }

  .mechanical-solution__feature-shell
    :deep(.feature-mosaic-grid__mobile--mechanical .feature-mosaic-card--photo .feature-mosaic-card__image-copy) {
    inset: clamp(8px, 2.2cqw, 14px) auto auto clamp(8px, 2.2cqw, 14px);
    right: clamp(8px, 2.2cqw, 14px);
  }

  .mechanical-solution__feature-shell
    :deep(.feature-mosaic-grid__mobile--mechanical .feature-mosaic-card--photo .feature-mosaic-card__image-copy h3) {
    font-size: clamp(var(--pixso-fs-10), 2.8vw, var(--pixso-fs-14));
    line-height: clamp(var(--pixso-lh-10), 3.4vw, var(--pixso-lh-14));
  }

  /* EN compact: only type metrics differ from Chinese. */
  .mechanical-solution--en .mechanical-solution__feature-shell
    :deep(.feature-mosaic-grid__mobile--mechanical .feature-mosaic-card--action .feature-mosaic-card__text h3) {
    font-size: clamp(var(--pixso-fs-10), 2.8vw, var(--pixso-fs-14));
    line-height: clamp(var(--pixso-lh-10), 3.4vw, var(--pixso-lh-14));
    font-weight: 400;
  }

  .mechanical-solution--en .mechanical-solution__feature-shell
    :deep(.feature-mosaic-grid__mobile--mechanical .feature-mosaic-card--group-topCards .feature-mosaic-card__text h3) {
    font-size: clamp(var(--pixso-fs-10), 2.5vw, var(--pixso-fs-12));
    line-height: clamp(var(--pixso-lh-10), 3.1vw, var(--pixso-lh-12));
  }

  .mechanical-solution--en .mechanical-solution__feature-shell
    :deep(.feature-mosaic-grid__mobile--mechanical .feature-mosaic-card--action .feature-mosaic-card__text p) {
    font-size: var(--pixso-fs-10);
    line-height: var(--pixso-lh-10);
    font-weight: 400;
  }

  .mechanical-solution--en .mechanical-solution__feature-shell
    :deep(.feature-mosaic-grid__mobile--mechanical .feature-mosaic-card--photo .feature-mosaic-card__image-copy h3) {
    font-size: clamp(var(--pixso-fs-10), 2.6vw, var(--pixso-fs-12));
    line-height: clamp(var(--pixso-lh-10), 3.1vw, var(--pixso-lh-12));
    font-weight: 400;
  }
}

@media (max-width: 768px) {
  .mechanical-solution {
    --mechanical-section-space: var(--mechanical-space-96);
    --mechanical-pain-top-space: var(--mechanical-space-96);
    --mechanical-pain-bottom-space: var(--mechanical-space-96);
    --mechanical-flow-gap: var(--pixso-gap-lg);
    padding-bottom: var(--mechanical-space-96);
  }

  .mechanical-solution__hero {
    height: var(--pixso-hero-mobile);
    min-height: var(--pixso-hero-mobile);
  }

  .mechanical-solution__hero-copy {
    top: clamp(96px, 18vw, 132px);
  }

  .mechanical-solution__media-card {
    border-radius: var(--mechanical-radius-sm);
  }

  .mechanical-solution__how-card-shell {
    width: min(calc(100% - var(--pixso-page-padding)), 960px);
    margin-top: 0;
  }

  .mechanical-solution__how-stage {
    gap: clamp(24px, 4vh, 40px);
    padding-block: clamp(76px, 10vh, 104px) clamp(20px, 4vh, 36px);
  }

  .mechanical-solution__how-card,
  .mechanical-solution__how-card .mechanical-solution__video {
    max-height: min(48vh, 420px);
  }

  .mechanical-solution__section-head {
    padding-inline: 0;
  }

  .mechanical-solution__stack-title > span {
    font-size: var(--pixso-fs-56);
    line-height: var(--pixso-lh-56);
  }
}


</style>
